import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from './UserContext';
import { toast } from 'react-toastify';

const SellerContext = createContext();

export const useSeller = () => {
    return useContext(SellerContext);
};

export const SellerProvider = ({ children }) => {
    const { user } = useUser();
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [returns, setReturns] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState({
        totalProducts: 0,
        totalOrders: 0,
        totalEarnings: 0
    });
    const [wallet, setWallet] = useState({
        totalEarnings: 0,
        withdrawn: 0,
        pendingClearance: 0,
        availableBalance: 0,
        recentPayouts: []
    });
    const [loading, setLoading] = useState(true);

    const fetchSellerData = async () => {
        if (!user || user.role !== 'seller') {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const [ordersRes, productsRes, walletRes, returnsRes, reviewsRes] = await Promise.all([
                axios.get('/api/orders/seller', config),
                axios.get('/api/products/seller', config),
                axios.get('/api/seller/wallet', config).catch(() => ({ data: { data: {} } })),
                axios.get('/api/returns/seller', config).catch(() => ({ data: { returns: [] } })),
                axios.get('/api/reviews/seller', config).catch(() => ({ data: { data: [] } }))
            ]);

            const fetchedOrders = ordersRes.data.orders || [];
            const fetchedProducts = productsRes.data.data || [];
            const fetchedWallet = walletRes.data.data || {};
            const fetchedReturns = returnsRes.data.returns || [];
            const fetchedReviews = reviewsRes.data.data || [];

            setOrders(fetchedOrders);
            setProducts(fetchedProducts);
            setReturns(fetchedReturns);
            setReviews(fetchedReviews);
            if (fetchedWallet) {
                setWallet(fetchedWallet);
            }

            // Calculate Stats
            setStats({
                totalProducts: fetchedProducts.length,
                totalOrders: fetchedOrders.length,
                totalEarnings: fetchedOrders
                    .filter(o => o.status === 'Delivered')
                    .reduce((acc, order) => acc + order.totalAmount, 0)
            });

        } catch (error) {
            console.error('Failed to fetch seller data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSellerData();
    }, [user]);

    // Actions
    const addProduct = async (productData) => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.post('/api/products', productData, config);
            
            if (data.success) {
                setProducts([data.data, ...products]);
                setStats(prev => ({ ...prev, totalProducts: prev.totalProducts + 1 }));
                toast.success('Product added successfully!');
                return { success: true };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to add product';
            toast.error(message);
            return { success: false, message };
        }
    };

    const updateProduct = async (id, productData) => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.put(`/api/products/${id}`, productData, config);
            
            if (data.success) {
                setProducts(products.map(p => p._id === id ? data.data : p));
                toast.success('Product updated successfully!');
                return { success: true };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to update product';
            toast.error(message);
            return { success: false, message };
        }
    };

    const deleteProduct = async (id) => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.delete(`/api/products/${id}`, config);
            
            if (data.success) {
                setProducts(products.filter(p => p._id !== id));
                setStats(prev => ({ ...prev, totalProducts: Math.max(0, prev.totalProducts - 1) }));
                toast.success('Product deleted successfully!');
                return { success: true };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to delete product';
            toast.error(message);
            return { success: false, message };
        }
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.put(`/api/orders/${orderId}/status`, { status: newStatus }, config);
            
            if (data.success) {
                const updatedOrders = orders.map(o => o._id === orderId ? { ...o, status: newStatus } : o);
                setOrders(updatedOrders);
                
                // Recalculate earnings in case a new order was delivered
                setStats(prev => ({
                    ...prev,
                    totalEarnings: updatedOrders
                        .filter(o => o.status === 'Delivered')
                        .reduce((acc, order) => acc + order.totalAmount, 0)
                }));
                
                toast.success('Order status updated!');
                return { success: true };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to update order status';
            toast.error(message);
            return { success: false, message };
        }
    };

    const requestPayout = async (amount, method) => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.post('/api/seller/payouts', { amount, method }, config);
            
            if (data.success) {
                toast.success('Withdrawal request submitted!');
                await fetchSellerData(); // Refresh wallet data
                return { success: true };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to request payout';
            toast.error(message);
            return { success: false, message };
        }
    };

    return (
        <SellerContext.Provider value={{
            products,
            orders,
            returns,
            reviews,
            stats,
            wallet,
            loading,
            refreshData: fetchSellerData,
            addProduct,
            updateProduct,
            deleteProduct,
            updateOrderStatus,
            requestPayout
        }}>
            {children}
        </SellerContext.Provider>
    );
};
